import React, { useState, useEffect } from 'react';
import Header from '../Header/Header';
import { useNavigate } from 'react-router-dom';
import { CustomTable, ConfirmationPopup, SuccessModal, Search, Pagination, Loader } from '@ohif/ui';
import './index.css';
import { customStyle } from '@custom/Utils/utils';
import {useTheme} from "../../ui/src/contextProviders"
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import deleteUser from '@custom/rtk-apis/user/deleteUser';
import activateUserApi from '@custom/rtk-apis/user/activateUser';
import deactivateRadiologistUserApi from '@custom/rtk-apis/user/deactivateRadiologistUser';
import userList from '@custom/rtk-apis/user/userList';
import { closeUserListModal } from '@custom/store/slices/user/getUserList';
import { closeDeactivateRadiologistUserListModal } from '@custom/store/slices/user/deactivateRadiologistUser';
import { closeUserDeleteModal } from '@custom/store/slices/user/deleteUser';

const Teleradiologist = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const { isDarkMode } = useTheme();
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [search, setSearch] = useState('');
  const [totalPg, setTotalPg] = useState(1000000);
  const [load, setLoad] = useState(true);
  const [title, setTitle] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [buttonText, setButtonText] = useState("Deactivated Radiologists");
  const [showDeleteAction, setShowDeleteAction] = useState(true);
  const [actionType, setActionType] = useState('');

  const { msg, result, loading, status } = useSelector((state: RootState) => state?.userListResponse);
  const {
    message: deactivateMsg,
    result: deactivateResult,
    loading: deactivateLoading,
    status: deactivateStatus
  } = useSelector((state: RootState) => state?.deactivateRadiologistUserSliceResponse);
  const deleteUserResponse = useSelector((state: RootState) => state?.deleteUserResponse);

  const columns = [
    { key: 'name', title: 'Name', sort: true },
    { key: 'radiologistType', title: 'Radiologist Type', sort: true },
    { key: 'role', title: 'Role', sort: true },
    { key: 'modality', title: 'Expertise' },
    { key: 'email', title: 'Email ID' },
    { key: 'hprId', title: 'HPR ID' },
    { key: 'contactDetails', title: 'Contact Details' },
    { key: 'licenseNumber', title: 'License Number' },
    { key: 'actions', title: 'Action' },
  ];

  const actions = [
    {
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        if (item?.userDetail?.status === 'delete') {
          setSelectedRow(item);
          setActionType('activate');
          setIsPopupOpen(true);
        } else {
          navigate("/addTeleradiologist", { state: { userData: item?.userDetail } });
        }
      }
    },
    ...(buttonText === "Deactivated Radiologists" ? [{
      label: 'Deactivate',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setActionType('deactivate');
        setIsPopupOpen(true);
      }
    }] : []),
  ];

  const handleActivateConfirm = (item) => {
    if (selectedRow?.userDetail?._id) {
      dispatch(activateUserApi({ userId: selectedRow.userDetail._id }));
      setIsSuccessModal(true);
      setTitle('Radiologists activated successfully.');
      setTimeout(() => {
        setIsPopupOpen(false);
        fetchUserList();
        setButtonText('Deactivated Radiologists');
      }, 300);
    } else {
      console.error('User ID is undefined');
    }
  };

  const handleConfirm = async () => {
    if (actionType === 'deactivate') {
      await dispatch(deleteUser({ _id: selectedRow?.userDetail?._id }));
    } else if (actionType === 'activate') {
      await handleActivateConfirm(selectedRow?.userDetail?._id);
    }
    setIsPopupOpen(false);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const navigateToPage = () => {
    navigate('/addTeleradiologist');
  };

  const fetchUserList = () => {
    const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
    let obj = {
      pgLimit: pgLimit,
      pageNumber: pgLimit * (pgNumber - 1),
      search: search,
      hospitalId: hospitalId
    }
    dispatch(userList(obj));
  };

  const handleDeactivateUser = async () => {
    if (buttonText === "Deactivated Radiologists") {
      setLoad(true);
      await dispatch(deactivateRadiologistUserApi());
      setButtonText("Activated Radiologists");
      setShowDeleteAction(false);
    } else {
      setLoad(true);
      await fetchUserList();
      setButtonText("Deactivated Radiologists");
      setShowDeleteAction(true);
    }
    setLoad(false);
  };

  const searchClick = (searchText) => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);
  };

  const onPageChangeHandler = (pg) => {
    setPgNumber(pg);
  };

  const handlePageSizeChange = (e) => {
    setPgLimit(e);
  };

  const changeRoute = (item) => {
    navigate('/user/details/' + item?.userDetail?._id, {
      state: { userData: item },
    });
  };

  useEffect(() => {
    fetchUserList();
  }, [search, pgLimit, pgNumber]);

  useEffect(() => {
    if (deleteUserResponse?.status === "SUCCESS") {
      setIsSuccessModal(true);
      setTitle('User deleted successfully.');
      setTimeout(() => {
        setIsSuccessModal(false);
        fetchUserList();
      }, 800);
    }
    dispatch(closeUserDeleteModal());
  }, [deleteUserResponse]);

  useEffect(() => {
    if (status === 'SUCCESS') {
      setTotalPg(result?.count);
      const tempArray = result?.list?.map((item) => ({
        name: item?.name
        ? item.name
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : '--',
        radiologistType: item?.radiologistType || '--',
        role: item?.role || '--',
        modality: Array.isArray(item?.modality) && item?.modality.length > 0 ? item?.modality.join(', ') : '--',
        hprId: item?.hprId || '--',
        email: item?.email || '--',
        contactDetails: `${item?.contactNumberCountryCode || ''} ${item?.contactNumber || ''}`,
        licenseNumber: item?.licenseNumber || '--',
        userDetail: item
      }));
      setTableData(tempArray);
    }
    else if (deactivateStatus === 'SUCCESS') {
      setTotalPg(deactivateResult?.length);
      const tempArray = deactivateResult?.map((item) => ({
        name: item?.name
        ? item.name
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : '--',
        radiologistType: item?.radiologistType || '--',
        role: item?.role || '--',
        modality: Array.isArray(item?.modality) && item?.modality.length > 0 ? item?.modality.join(', ') : '--',
        hprId: item?.hprId || '--',
        email: item?.email || '--',
        contactDetails: `${item?.contactNumberCountryCode || ''} ${item?.contactNumber || ''}`,
        licenseNumber: item?.licenseNumber || '--',
        userDetail: item
      }));
      setTableData(tempArray);
    }
    dispatch(closeUserListModal());
    dispatch(closeDeactivateRadiologistUserListModal());
  }, [status, deactivateStatus, result, deactivateResult]);

  return (
    <>
      <div className='page page__fullBody' style={
        isDarkMode ? {background: '#090c29'} : customStyle.mainBackground
    }>
        <Header isBack={false} title='Teleradiologist'></Header>
        <div className='page__body'>
          <div className='tableWrap'>
            <div className='tableWrap__head'>
              <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search text="Search by Name" onSearchClick={searchClick} value={searchInputValue} setValue={setSearchInputValue}></Search>
                </div>
              </div>
              <div className='btn__group'>
                <button className="btn btn__white" onClick={handleDeactivateUser}>
                  {buttonText}
                </button>
                <button className="btn btn__white" onClick={navigateToPage}>
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684" stroke="#150823" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Add Radiologist
                </button>
              </div>
            </div>
            <div className='tableContainer'>
              {loading || deactivateLoading ? <Loader isLoaderOpen={loading ||deactivateLoading} /> : <CustomTable columns={columns} data={tableData} actions={actions} searchQuery={searchQuery} userDetails={changeRoute} from="user" setClickedItem={''} clickedItem={''} />}
            </div>
          </div>
        </div>
        {tableData?.length > 0 && <Pagination
          onChangePage={onPageChangeHandler}
          onChangePerPage={handlePageSizeChange}
          totalPage={totalPg}
          currentPage={pgNumber}
          perPage={pgLimit}
        />}
      </div>
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to ${actionType} ${selectedRow?.name}?`}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={''}
      />
      <SuccessModal
        isSuccessModalOpen={isSuccessModalOpen}
        title={title}
        handleCloseModal={() => setIsSuccessModal(false)}
      />
    </>
  );
}

export default Teleradiologist;
